// The whole code is copied from VPC.

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import { DataGrid } from '@tableau/widgets-datagrid';
import { Messages } from '../../../../gen-src/Localize';
import { L10n } from '../../../common/utils/L10n';
import { sanitize } from '../../../common/utils/Sanitize';
import { TextileHelper } from '../../../common/utils/TextileHelper';
import { IOrderClause } from '../../models/sort/IOrderClause';
import { OrderKeys } from '../../models/sort/OrderKeys';
import { ListViewStyles } from './ListViewStyles';
import { ILink, LinkCell } from './dataGridCells/LinkCell';
import { CommonColumn } from './types/Columns';
import { ContentDefinitions } from '../../models/contentDefinitions';
import { ContentType } from '../../models/ContentType';
import { ILocationInfo, LocationTypes } from '../../models/ILocationInfo';
import { SessionManagementService } from '../../services/SessionManagementService';
import { defaultHeaderFormatter } from './DefaultHeaderFormatter';
import { AppUrls } from '../../../app/AppUrls';
import { PlainTextCell } from './dataGridCells/PlainTextAndNumberCells';
import { FavoriteCell } from './dataGridCells/FavoriteCell';

export const IconColumnPreferredWidth = 44;

export type ColumnType = CommonColumn;

export interface ListViewGridColumnGeneratorParams {
  readonly displayingItems: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly contentType: ContentType;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly urls: AppUrls;
  readonly searchTerm: string; //used for telemetry
  readonly total: number; //used for telemetry
}

export class ListViewGridColumnGenerator {
  private params: ListViewGridColumnGeneratorParams;

  public constructor(params: ListViewGridColumnGeneratorParams) {
    this.params = params;
  }

  public updateParams(paramOverrides: Partial<ListViewGridColumnGeneratorParams>) {
    this.params = { ...this.params, ...paramOverrides };
  }

  public getColumn(columnFactoryMethod: ColumnFactoryMethod): DataGrid.Column | null {
    return columnFactoryMethod({
      generateLinkForName: this.generateLinkForName,
      generateLinkForOwner: this.generateLinkForOwner,
      generateLinkForLocation: this.generateLinkForLocation,
      listViewParams: this.params,
    });
  }

  private isAccessDenied = (row: DataGrid.Row): boolean => {
    return !!row.data.isAccessDenied;
  };

  private getContentUrl = (row: DataGrid.Row): string => {
    const contentSpaceUrlHandler = ContentDefinitions[this.params.contentType].contentSpaceUrl;
    //the collections urls use luids instead of regular ids, so we need to pass both the row key and the luid
    return contentSpaceUrlHandler ? contentSpaceUrlHandler(this.params.urls, row.key, row.data.luid) : '';
  };

  private generateLinkForLocation = (row: DataGrid.Row): ILink => {
    const userId = SessionManagementService.instance.sessionUserId();

    const location = row.data.location;
    const isOwner: boolean = row.data.owner && userId === row.data.owner.id;
    if (location) {
      return this.getLinkForLocationType(location, isOwner);
    } else if (row.data.project) {
      return {
        text: row.data.project.name,
        url: this.params.urls.projectUrl(row.data.project.id),
      };
    }

    return { text: '', url: '' };
  };

  private getLinkForLocationType = (location: ILocationInfo, isOwner: boolean): ILink => {
    switch (location.type) {
      case LocationTypes.PersonalSpace:
        return {
          text: Messages.personalSpaceGridLabel(),
          url: isOwner ? this.params.urls.personalSpaceUrl() : undefined,
          displayJustText: !isOwner,
        };
      case LocationTypes.Project:
        return {
          text: location.name,
          url: this.params.urls.projectUrl(location.id),
        };
      default:
        return {
          text: '',
          url: '',
        };
    }
  };
  /*

  private generateLinkForDataconnectionDatasource = (row: DataGrid.Row): ILink => {
    if (!row.data.datasource) {
      // TODO check to see if this is a flow data connection for now return an empty link
      return { url: '', text: '' };
    }

    // we want to display the datasource information here, so grab the data source
    // Throw together object that adheres to DataGrid.Row interface and generate link for this ds
    const modifiedDatasourceRow = {
      key: row.data.datasource.id,
      data: DataSources.getDatasourceInfoFromDataConnection(row.data as IDataConnectionInfo),
    } as DataGrid.Row;
    return this.generateLinkForName(modifiedDatasourceRow, this.dataConnectionDataSourceCellTestId(row));
  };
*/
  // Following logic is borrowed from app/content/ProjectGrid.ts
  private generateLinkForName = (row: DataGrid.Row, testId?: string): ILink => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, sanitize);
    const title = description ? name + '\n' + description : name;

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.getContentUrl(row),
      displayJustText: false,
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  };
  /*
  // will only render status for flows or workbooks which support drafts, otherwise will just get the correct url based on the content type
  private generateLinkForNameWithDraft = (row: DataGrid.Row, testId?: string): ILinkWithDraftStatus => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, sanitize);
    const title = description ? name + '\n' + description : name;
    const sessionManagementService = getSessionManagementServiceInstance();
    const userId = sessionManagementService.sessionUserId();
    const isOwner: boolean = row.data.owner && userId === row.data.owner.id;
    const displayJustText: boolean =
      (isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row)) || (this.isNeverPublishedFlow(row) && !isOwner);

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.isNeverPublishedFlow(row) ? this.params.urls.flowEditorDraftUrl(row.data.draftLuid) : this.getContentUrl(row),
      displayJustText: displayJustText,
      testId: testId ? testId : 'name-' + row.key,
      draftStatus: row.data.draftStatus,
      openLinkInNewtab: this.isNeverPublishedFlow(row),
    };
  };

  private generateLinkForTasksFlowName = (row: DataGrid.Row, testId?: string): ILink => {
    const urls = this.params.urls.forSiteName(row.data.site.urlName);
    if (row.data.flow) {
      return {
        text: row.data.flow.name,
        url: urls.flowUrl(row.data.flow.id),
        iconClassName: 'tb-grid-icon-flow',
        iconTitle: Messages.flow(),
      };
    }
  };

  private generateLinkForWorkbookDatasourceName = (row: DataGrid.Row, testId?: string): ILink => {
    const name = row.data && row.data.workbook ? row.data.workbook.name : row.data.datasource.name;
    const urls = this.params.urls.forSiteName(row.data.site.urlName);
    if (row.data.datasource) {
      const datasource: IDataSourceInfo = row.data.datasource;
      return {
        text: datasource.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.datasourceUrl(datasource.id),
        iconClassName: 'tb-grid-icon-datasource',
        iconTitle: Messages.datasource(),
      };
    } else {
      const workbook: IWorkbookInfo = row.data.workbook;
      return {
        text: workbook.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.workbookUrl(workbook.id),
        iconClassName: 'tb-grid-icon-workbook',
        iconTitle: Messages.workbook(),
      };
    }
  };
*/
  private generateTitleForName = (row: DataGrid.Row, title: string): string => {
    if (this.isEmbeddedDatasource(row)) {
      return Messages.datasourceEmbeddedInWorkbook_nameTooltip({
        DATASOURCE_NAME: row.data.name,
        WORKBOOK_NAME: row.data.workbook.name,
      });
    }

    return L10n.elideIfLong(title, 511); // IE only allows up to 512 characters in tooltips (unlike Firefox/Chrome)
  };

  private isEmbeddedDatasource = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'datasource' && !!row.data.workbook;
  };

  /* private generateLinkForOriginalViewName = (row: DataGrid.Row, testId?: string): ILink => {
    const viewName = row.data.viewName;

    const url = this.params.urls.redirectToViewUrl(row.data.viewId);

    return {
      text: viewName,
      column: CommonColumn.OriginalViewName,
      url: url,
      displayJustText: isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row),
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  }; */

  private generateLinkForOwner = (row: DataGrid.Row): ILink => {
    // detail view info's stores owner as part of workbook object
    const useWorkbookOwner = row.data.contentType === 'view';
    const owner = useWorkbookOwner && row.data.workbook ? row.data.workbook.owner : row.data.owner;

    if (!owner) {
      return { text: '', url: '' };
    }

    const displayName = owner.displayName;
    const url = this.params.urls.userUrl(owner.username, owner.domainName);

    const displayHiddenUser = owner.isHiddenInternalUser || SessionManagementService.instance.isLimitedUserVisibilityChannel();

    return {
      text: displayHiddenUser ? Messages.hiddenUser() : displayName,
      url: url,
      style: displayHiddenUser ? ListViewStyles.HiddenUserStyle : null,
      displayJustText: displayHiddenUser,
      testId: 'list-owner-cell-' + row.data.contentType + '-' + row.data.id,
      isAccessDenied: this.isAccessDenied(row),
    };
  };
  /*
  private generateLinkForOwnerWithIcon = (row: DataGrid.Row): ILink => {
    const linkCell: ILink = this.generateLinkForOwner(row);

    linkCell.iconClassName = 'tb-grid-icon-user';
    linkCell.iconTitle = Messages.user();

    return linkCell;
  };

  private generateLinkForMetricConnectedView = (row: DataGrid.Row): ILink => {
    if (row.data && row.data.connectedView) {
      const connectedView: IConnectedViewInfo = row.data.connectedView;
      const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;

      const url = this.params.urls.viewUrl(connectedView.path);

      return {
        text: viewName,
        url: url,
      };
    } else {
      return {
        text: Messages.unavailable(),
        url: '',
        style: GreyedOutTextStyle,
        displayJustText: true,
      };
    }
  };

  private generateLinkForSubscribedBy = (row: DataGrid.Row): ILink => {
    const subscription = row.data as ISubscriptionInfo;
    const subscribedBy = subscription.creator;
    if (!subscribedBy) {
      return { text: '', url: undefined };
    }
    const displayName = subscribedBy.displayName;
    const url = new SpacesSiteUrls({ siteUrlName: subscription.site.urlName }).userUrl(subscribedBy.username, subscribedBy.domainName);
    return {
      text: displayName,
      url: url,
      style: subscribedBy.isHiddenInternalUser ? (HiddenUserStyles.HiddenUserStyle as React.CSSProperties) : null,
      displayJustText: subscribedBy.isHiddenInternalUser,
    };
  };

  private generateLinkForModifiedBy = (row: DataGrid.Row): ILink => {
    const useWorkbookModifiedBy = !this.params.showingMixedContent && row.data.contentType === 'workbook';
    const modifiedBy = useWorkbookModifiedBy && row.data.workbook ? row.data.workbook.modifiedBy : row.data.modifiedBy;

    if (!modifiedBy) {
      return { text: '', url: undefined };
    }

    const displayName = modifiedBy.displayName;
    const url = this.params.urls.userUrl(modifiedBy.username, modifiedBy.domainName);

    return {
      text: displayName,
      url: url,
      style: modifiedBy.isHiddenInternalUser ? (HiddenUserStyles.HiddenUserStyle as React.CSSProperties) : null,
      displayJustText: modifiedBy.isHiddenInternalUser,
    };
  };

  private generateLinkForWorkbook = (row: DataGrid.Row): ILink => {
    return (
      row.data.workbook && {
        text: row.data.workbook.name,
        url: this.params.urls.workbookUrl(row.data.workbook.id),
      }
    );
  };

  private generateAlert = (row: DataGrid.Row): IAlert => {
    return {
      type: row.data.contentType,
    };
  };

  private generateLinkForSiteName = (row: DataGrid.Row): ILink => {
    const site = row.data as ISiteSettingsForServerAdmin;
    return {
      text: site.settings.name,
      url: this.params.urls.forSiteName(site.settings.urlName).projectsUrl(),
      iconClassName: 'tb-grid-icon-site-stylish',
      iconTitle: Messages.site(),
    };
  };

  private generateLinkForScheduleName = (row: DataGrid.Row): ILink => {
    const schedule = row.data as IScheduleInfo;
    let displayName = schedule.name;
    if (this.params.displayScheduleDescriptionAsName) {
      displayName = displayName + ' - ' + IScheduleInfo.getScheduleDescription(schedule);
    }
    return {
      text: displayName,
      url: this.params.inServerArea
        ? serverScheduleUrl(ServerUrls.getInstance(), schedule.id, schedule.scheduledAction)
        : siteScheduleUrl(this.params.urls, schedule.id, schedule.scheduledAction),
      iconClassName: 'tb-grid-icon-schedule-stylish',
      iconTitle: Messages.schedule(),
    };
  };

  private generateLinkForGroupName = (row: DataGrid.Row): ILink => {
    const group = row.data as IGroupInfo;
    const displayName = group.name;
    return {
      text: displayName,
      url: this.params.urls.groupUrl(group.id),
      // TFSID: 1186001 - added to prevent browser truncation of whitespace in the display text.
      style: { whiteSpace: 'pre' },
      iconClassName: 'tb-grid-icon-group-stylish',
      iconTitle: Messages.group(),
    };
  };

  private generateActionsOptions = (): ActionCellOptions => {
    return {
      getContentItem: (row: DataGrid.Row) => row.data as IContentInfo,
      isRowSelected: this.isRowSelected,
      isAccessDenied: this.isAccessDenied,
      isRowDisabled: this.isRowDisabled,
    };
  };

  private showingOnlyDataConnections = (): boolean => {
    return (
      (this.params.contentFilter.length === 1 && this.params.contentFilter[0] === 'dataconnection') ||
      isDatasourceConnectionSubSpace(this.params.spaceInfo)
    );
  };

  private siteUserDataGridColumn = (sortableHeaderFormatter: any): DataGrid.Column => {
    return {
      name: CommonColumn.SiteUserDisplayName,
      dataKey: OrderKeys.displayName,
      sortable: true,
      direction: 'asc',
      growToFit: true,
      headerText: Messages.column_fullName(),
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: UserDataGridCells.ProfilePictureCell(
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          return {
            profileDisplayName: user.displayName,
            imageUrl: user.userImageUrl,
            divShape: ProfilePicture.Shape.Circle,
          };
        },
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          const disabled = user.siteRole === SiteRoles.Guest;
          return {
            text: user.displayName,
            url: disabled ? null : this.params.urls.userUrl(user.username, user.domainName),
            style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null,
          };
        }
      ),
    };
  }; */
}

export type ColumnFactoryMethod = (parameters: IColumnFactoryParameters) => DataGrid.Column;

export interface IColumnFactoryParameters {
  generateLinkForName: (row: DataGrid.Row, testId?: string) => ILink;
  generateLinkForOwner: (row: DataGrid.Row) => ILink;
  generateLinkForLocation: (row: DataGrid.Row) => ILink;
  listViewParams: ListViewGridColumnGeneratorParams;
}

export interface ICommonColumns {
  name: ColumnFactoryMethod;
  owner: ColumnFactoryMethod;
  location: ColumnFactoryMethod;
  description: ColumnFactoryMethod;
  favorite: ColumnFactoryMethod;
}

export const CommonColumns: ICommonColumns = {
  //The name column is the only column that has a SearchResultClicked telemetry event associated
  name: (parameters: IColumnFactoryParameters): DataGrid.Column => {
    return {
      name: CommonColumn.Name,
      dataKey: OrderKeys.name,
      sortable: false,
      headerText: Messages.column_name(),
      direction: 'asc',
      growToFit: true,
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(
        parameters.generateLinkForName,
        true,
        parameters.listViewParams.urls,
        parameters.listViewParams.searchTerm,
        parameters.listViewParams.total
      ),
    };
  },
  owner: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'owner',
      dataKey: 'ownerName',
      sortable: false,
      headerText: Messages.column_owner(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(parameters.generateLinkForOwner),
      ...{ growToFit: true },
    };
  },
  location: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'location',
      dataKey: 'containerName',
      sortable: false,
      headerText: Messages.column_location(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(parameters.generateLinkForLocation),
      ...{ growToFit: true },
    };
  },
  description: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'description',
      dataKey: 'description',
      sortable: false,
      headerText: Messages.column_description(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: PlainTextCell('description'),
      ...{ growToFit: true },
    };
  },
  favorite: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'favorite',
      dataKey: 'favorite',
      sortable: false,
      headerText: '',
      preferredWidth: IconColumnPreferredWidth,
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: FavoriteCell(),
    };
  },
};
